import React from "react";
import type {
  AriaCalendarCellProps,
  AriaCalendarGridProps,
  CalendarProps,
  DateValue,
} from "react-aria";
import {
  useCalendar,
  useCalendarCell,
  useCalendarGrid,
  useDateFormatter,
  useLocale,
} from "react-aria";
import type { CalendarState, RangeCalendarState } from "react-stately";
import { useCalendarState } from "react-stately";
import type { CalendarDate, DateDuration } from "@internationalized/date";
import {
  createCalendar,
  endOfMonth,
  getWeeksInMonth,
  isSameDay,
  isSameMonth,
} from "@internationalized/date";
import { styled } from "@linaria/react";
import Text from "../Text";
import ButtonIconV2 from "../ButtonIconV2";
import {
  ArrowLeftDoubleLine,
  ArrowLeftSLine,
  ArrowRightDoubleLine,
  ArrowRightSLine,
} from "../IconV2/react";
import { color } from "..";
import { css } from "@linaria/core";
import classNames from "classnames";
import { colors } from "../Theme";

const CustomButtonIconV2 = styled(ButtonIconV2)`
  padding: 4px;
`;

export const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 280px;

  padding: 12px;

  border-bottom: 1px solid ${color.titanium100};
`;

export const CalendarTitle = styled(Text)`
  color: ${color.carbon800};
  text-transform: capitalize;
`;

const CalendarGridWrapper = styled.div`
  padding: 8px 12px;
`;

const CalendarCellWrapperClassName = css`
  display: flex;
  padding: 4px 0px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;
`;

const CalendarTable = styled.table`
  width: 100%;
  color: ${color.carbon900};

  tr {
    display: flex;
    width: 256px;
    align-items: center;
  }
`;

function Calendar<T extends DateValue>(props: CalendarProps<T>) {
  const { locale } = useLocale();
  const state = useCalendarState({
    ...props,
    locale,
    createCalendar,
  });

  const { calendarProps, prevButtonProps, nextButtonProps } = useCalendar({ ...props }, state);

  delete prevButtonProps.onPress;
  delete nextButtonProps.onPress;

  delete nextButtonProps.onFocusChange;
  delete prevButtonProps.onFocusChange;

  const handlePrevButtonClick = state.focusPreviousPage;
  const handleNextButtonClick = state.focusNextPage;

  const formatter = useDateFormatter({
    month: "short",
    year: "numeric",
    timeZone: state.timeZone,
  });

  const handleNextYearButtonClick = React.useCallback(() => {
    state.focusNextSection(true);
  }, [state]);

  const handlePrevYearButtonClick = React.useCallback(() => {
    state.focusPreviousSection(true);
  }, [state]);

  const titleDate = formatter.format(state.focusedDate.toDate(state.timeZone));

  return (
    <div {...calendarProps}>
      <CalendarHeader>
        <div>
          <CustomButtonIconV2
            onClick={e => {
              e.stopPropagation();
              handlePrevYearButtonClick();
            }}
            aria-label="Année Précédente"
          >
            <ArrowLeftDoubleLine />
          </CustomButtonIconV2>
          <CustomButtonIconV2
            {...prevButtonProps}
            onClick={e => {
              e.stopPropagation();
              handlePrevButtonClick();
            }}
          >
            <ArrowLeftSLine />
          </CustomButtonIconV2>
        </div>
        <CalendarTitle type="body1SemiBold">{titleDate}</CalendarTitle>
        <div>
          <CustomButtonIconV2
            {...nextButtonProps}
            onClick={e => {
              e.stopPropagation();
              handleNextButtonClick();
            }}
          >
            <ArrowRightSLine />
          </CustomButtonIconV2>
          <CustomButtonIconV2
            onClick={e => {
              e.stopPropagation();
              handleNextYearButtonClick();
            }}
            aria-label="Année Suivante"
          >
            <ArrowRightDoubleLine />
          </CustomButtonIconV2>
        </div>
      </CalendarHeader>
      <CalendarGrid state={state} />
    </div>
  );
}

interface CalendarGridProps extends AriaCalendarGridProps {
  state: CalendarState | RangeCalendarState;
  offset?: DateDuration;
}

export function CalendarGrid({ state, offset, ...props }: CalendarGridProps) {
  const { locale } = useLocale();

  const startDate = state.visibleRange.start.add(offset ?? {});
  const endDate = endOfMonth(startDate);

  const { gridProps, headerProps, weekDays } = useCalendarGrid(
    { ...props, startDate, endDate },
    state,
  );

  // Get the number of weeks in the month so we can render the proper number of rows.
  const weeksInMonth = getWeeksInMonth(startDate, locale);

  return (
    <CalendarGridWrapper {...gridProps}>
      <CalendarTable>
        <thead {...headerProps}>
          <tr>
            {weekDays.map((day, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <th key={i} className={CalendarCellWrapperClassName}>
                <CellContent>
                  <Text>{day}</Text>
                </CellContent>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...Array(weeksInMonth).keys()].map(weekIndex => (
            <tr key={weekIndex}>
              {state.getDatesInWeek(weekIndex, startDate).map((date, i) =>
                date ? (
                  // eslint-disable-next-line react/no-array-index-key
                  <CalendarCell key={i} state={state} date={date} currentMonth={startDate} />
                ) : (
                  // eslint-disable-next-line react/no-array-index-key
                  <td className={CalendarCellWrapperClassName} key={i} />
                ),
              )}
            </tr>
          ))}
        </tbody>
      </CalendarTable>
    </CalendarGridWrapper>
  );
}

const CellContent = styled.div`
  display: flex;
  width: 26px;
  height: 26px;
  padding: 3px 4px;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  color: ${color.carbon900};
`;

const CellButton = styled.button`
  display: flex;
  width: 26px;
  height: 26px;
  padding: 3px 4px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 4px;

  background: transparent;
  border: 1px solid transparent;

  color: ${color.carbon900};

  &:hover {
    cursor: pointer;
    background-color: ${color.titanium100};
  }

  &.selected:not(.isBetweenSelection) {
    background-color: ${color.lightNitrogen500_Main};
    color: ${color.hydrogen};
  }

  &.focused {
    position: relative;
    box-shadow:
      0 0 0 1px ${colors.lightNitrogen500_MainFade075},
      0 0 0 2px ${colors.lightNitrogen500_MainFade060};
  }

  &.outsideVisibleRange {
    visibility: hidden;
  }

  &.unavailable {
    background-color: ${color.titanium50};
    color: ${color.carbon900};
    cursor: default;

    &:focus {
      border: 1px solid ${color.lightNitrogen500_Main};
    }

    & > * {
      opacity: 0.5;
    }
  }

  &.isRange {
    &.isSelectionStart,
    &.isSelectionEnd,
    &.isBetweenSelection {
      width: 100%;
    }

    &.isBetweenSelection {
      border-radius: 0px;
      background-color: ${color.lightNitrogen50};
    }

    &.isBetweenSelection:hover {
      background-color: ${color.lightNitrogen100};
    }

    &.isSelectionStart:not(.isRoundedRight) {
      border-top-right-radius: 0px;
      border-bottom-right-radius: 0px;
    }

    &.isSelectionEnd:not(.isRoundedLeft) {
      border-top-left-radius: 0px;
      border-bottom-left-radius: 0px;
    }
  }
`;

interface CalendarCellProps extends AriaCalendarCellProps {
  state: CalendarState | RangeCalendarState;
  currentMonth: CalendarDate;
}

function isRangeCalendarState(
  state: CalendarState | RangeCalendarState,
): state is RangeCalendarState {
  // check if 'state' has the properties defined in 'RangeCalendarState'
  return "highlightedRange" in state;
}

function CalendarCell({ state, currentMonth, ...props }: CalendarCellProps) {
  const ref = React.useRef(null);
  const { date } = props;

  const {
    cellProps,
    buttonProps,
    isSelected,
    isOutsideVisibleRange,
    isDisabled,
    isUnavailable,
    formattedDate,
    isFocused,
  } = useCalendarCell(props, state, ref);

  const isOutsideMonth = !isSameMonth(currentMonth, date);

  // The start and end date of the selected range will have
  // an emphasized appearance.

  const isRange = isRangeCalendarState(state);

  const isSelectionStart =
    isRangeCalendarState(state) && state.highlightedRange
      ? isSameDay(date, state.highlightedRange.start)
      : isSelected;
  const isSelectionEnd =
    isRangeCalendarState(state) && state.highlightedRange
      ? isSameDay(date, state.highlightedRange.end)
      : isSelected;

  // We add rounded corners on the left for the first day of the month,
  // the first day of each week, and the start date of the selection.
  // We add rounded corners on the right for the last day of the month,
  // the last day of each week, and the end date of the selection.
  const isRoundedLeft = isSelected && isSelectionStart;
  const isRoundedRight =
    isSelected && (isSelectionEnd || date.day === date.calendar.getDaysInMonth(date));

  return (
    <td {...cellProps} className={CalendarCellWrapperClassName}>
      {/* <CellButtonWrapper> */}
      <CellButton
        {...buttonProps}
        ref={ref}
        className={classNames("cell", {
          selected: isSelected,
          disabled: isDisabled,
          unavailable: isUnavailable,
          focused: isFocused,
          outsideVisibleRange: isOutsideVisibleRange || (isRange && isOutsideMonth),
          isRoundedLeft,
          isRoundedRight,
          isSelectionStart,
          isSelectionEnd,
          isBetweenSelection: isSelected && !isSelectionStart && !isSelectionEnd,
          isRange,
        })}
      >
        <Text type={isSelectionStart || isSelectionEnd ? "body1SemiBold" : "body1"} as="span">
          {formattedDate}
        </Text>
      </CellButton>
      {/* </CellButtonWrapper> */}
    </td>
  );
}

export default Calendar;
